import { QueryStatus } from '@reduxjs/toolkit/query';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wizard } from 'react-use-wizard';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
import { HeadingGroup } from '@/components/ui/heading-group';
import { strings } from '@/constants/strings';
import { useCurrentGroup } from '@/hooks/use-current-group';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { useUnitUrlComposer } from '@/hooks/use-unit-url-composer';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { api } from '@/redux/api';
import { useAddDeviceToUnitMutation } from '@/redux/api/endpoints/device';
import { DeviceAssignedToOrganisation } from '@/types/device';
import { Resident } from '@/types/resident';
import { NewUnitWizardHeader } from './new-unit-wizard-header';
import { AssociateDevicesStep } from './steps/associate-devices-step';
import { AssociateResidentsStep } from './steps/associate-resident-step';
import { ConfirmationStep } from './steps/confirmation-step';
import { UnitDetailsStep } from './steps/unit-details-step';

const stepTitles: string[] = [
  'Unit details',
  'Associate resident(s)',
  'Associate device(s)',
  'Confirmation',
];

export const NewUnitPage: FC = () => {
  const [residentAssociationAttempts, setResidentAssociationAttempts] =
    useState(0);
  const [deviceAssociationAttempts, setDeviceAssociationAttempts] = useState(0);
  const [statusMessage, setStatusMessage] = useState<{
    message?: string;
    variant?: 'success' | 'error';
  }>({ message: undefined, variant: 'success' });
  const [createUnitQueryStatus, setCreateUnitQueryStatus] =
    useState<QueryStatus>(QueryStatus.uninitialized);
  const [associateResidentsQueryStatus, setAssociateResidentsQueryStatus] =
    useState<QueryStatus>(QueryStatus.uninitialized);
  const [associateDevicesQueryStatus, setAssociateDevicesQueryStatus] =
    useState<QueryStatus>(QueryStatus.uninitialized);
  const [createdUnitDatabaseId, setCreatedUnitDatabaseId] =
    useState<number>(-1);
  const [unitName, setUnitName] = useState<string | undefined>(undefined);
  const [selectedResidents, setSelectedResidents] = useState<Resident[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<
    DeviceAssignedToOrganisation[]
  >([]);
  const { organisation: currentOrganisation } = useCurrentOrganisation();
  const [createUnit] = api.useCreateUnitMutation();
  const [addDeviceToUnit] = useAddDeviceToUnitMutation();
  const [addResidentToUnit] = api.useAddResidentToUnitMutation();
  const navigate = useNavigate();
  const composeUnitUrl = useUnitUrlComposer();
  const { data: group } = useCurrentGroup();

  const handleResidentSelect = useCallback(
    (resident: Resident) => {
      setSelectedResidents((state) => {
        // TODO: add helper addItemIfMissing
        const updatedState = [...state];
        const existingIndex = updatedState.findIndex(
          ({ residentId }) => resident.residentId === residentId,
        );
        if (existingIndex === -1) {
          updatedState.push(resident);
        }
        return updatedState;
      });
    },
    [setSelectedResidents],
  );

  const handleResidentRemove = useCallback(
    (resident: Resident) => {
      setSelectedResidents((state) => {
        // TODO: add helper removeItemIfPresent
        const updatedState = [...state];
        const indexToRemove = updatedState.findIndex(
          ({ residentId }) => resident.residentId === residentId,
        );
        if (indexToRemove > -1) {
          updatedState.splice(indexToRemove, 1);
        }
        return updatedState;
      });
    },
    [setSelectedResidents],
  );

  const goToUnitPage = useCallback(() => {
    if (createdUnitDatabaseId) {
      navigate(composeUnitUrl(createdUnitDatabaseId));
    }
  }, [createdUnitDatabaseId, composeUnitUrl, navigate]);

  const hasPendingQuery = [
    createUnitQueryStatus,
    associateResidentsQueryStatus,
    associateDevicesQueryStatus,
  ].includes(QueryStatus.pending);

  // TODO: split into variables so it's more readable
  const allowSkipDeviceAssociation =
    createUnitQueryStatus === QueryStatus.fulfilled &&
    (residentAssociationAttempts > 1 ||
      (residentAssociationAttempts > 0 &&
        associateResidentsQueryStatus === QueryStatus.rejected) ||
      deviceAssociationAttempts > 1 ||
      (deviceAssociationAttempts > 0 &&
        associateDevicesQueryStatus === QueryStatus.rejected));

  const setErrorMessage = useCallback((message: string) => {
    setStatusMessage({ message, variant: 'error' });
  }, []);

  const setSuccessMessage = useCallback((message: string) => {
    setStatusMessage({ message, variant: 'success' });
  }, []);

  useEffect(() => {
    if (
      createUnitQueryStatus === QueryStatus.fulfilled &&
      associateResidentsQueryStatus === QueryStatus.fulfilled &&
      associateDevicesQueryStatus === QueryStatus.fulfilled
    ) {
      goToUnitPage();
    }
  }, [
    associateDevicesQueryStatus,
    associateResidentsQueryStatus,
    createUnitQueryStatus,
    goToUnitPage,
  ]);

  // TODO: move this logic to a function and add tests
  useEffect(() => {
    if (createUnitQueryStatus === QueryStatus.rejected) {
      setErrorMessage('An error occurred while creating the unit.');
      return;
    }

    if (createUnitQueryStatus === QueryStatus.fulfilled) {
      const associatingResidentsForTheFirstTime =
        associateResidentsQueryStatus === QueryStatus.pending &&
        residentAssociationAttempts < 2;
      const associatingDevicesForTheFirstTime =
        associateDevicesQueryStatus === QueryStatus.pending &&
        deviceAssociationAttempts < 2;
      if (
        associatingResidentsForTheFirstTime ||
        associatingDevicesForTheFirstTime
      ) {
        setSuccessMessage(
          `The unit ${unitName} has been successfully created, now associating ${associatingResidentsForTheFirstTime ? 'residents' : 'devices'}(s)`,
        );
        return;
      }

      const associatingResidentsSucceeded =
        associateResidentsQueryStatus === QueryStatus.fulfilled;
      const associatingDevicesSucceeded =
        associateDevicesQueryStatus === QueryStatus.fulfilled;

      const associatingResidentsFailed =
        associateResidentsQueryStatus === QueryStatus.rejected;
      const associatingDevicesFailed =
        associateDevicesQueryStatus === QueryStatus.rejected;

      const onlyResidentsFailed =
        associatingResidentsFailed && associatingDevicesSucceeded;
      const onlyDevicesFailed =
        associatingResidentsSucceeded && associatingDevicesFailed;
      const residentsAndDevicesFailed =
        associatingResidentsFailed && associatingDevicesFailed;

      if (onlyResidentsFailed || onlyDevicesFailed) {
        setErrorMessage(
          `Failed to associate the ${onlyResidentsFailed ? 'resident' : 'device'}(s) to the unit, You can try again or skip for now.`,
        );
        return;
      }

      if (residentsAndDevicesFailed) {
        setErrorMessage(
          `Failed to associate the resident(s) and device(s) to the unit, You can try again or skip for now.`,
        );
        return;
      }

      if (associateResidentsQueryStatus === QueryStatus.fulfilled) {
        setSuccessMessage(`The unit ${unitName} has been successfully created`);
        return;
      }
    }
  }, [
    associateDevicesQueryStatus,
    associateResidentsQueryStatus,
    createUnitQueryStatus,
    deviceAssociationAttempts,
    residentAssociationAttempts,
    unitName,
    setErrorMessage,
    setSuccessMessage,
  ]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleConfirm = async () => {
    if (!currentOrganisation || !unitName || hasPendingQuery) {
      return;
    }

    let id = createdUnitDatabaseId;
    // create unit - start
    if (createUnitQueryStatus !== QueryStatus.fulfilled) {
      setCreateUnitQueryStatus(QueryStatus.pending);
      const createUnitResult = await createUnit({
        organisation: currentOrganisation,
        unitName: unitName,
        groupId: group?.id,
      });
      if ('error' in createUnitResult) {
        // TODO: show error state
        console.error(createUnitResult.error);
        setCreateUnitQueryStatus(QueryStatus.rejected);
        return;
      }
      setCreateUnitQueryStatus(QueryStatus.fulfilled);
      id = createUnitResult.data.id;
      setCreatedUnitDatabaseId(id);
    }
    // create unit - end

    // associate residents - start
    if (selectedResidents.length === 0) {
      setAssociateResidentsQueryStatus(QueryStatus.fulfilled);
    } else if (associateResidentsQueryStatus !== QueryStatus.fulfilled) {
      setResidentAssociationAttempts((state) => ++state);
      setAssociateResidentsQueryStatus(QueryStatus.pending);

      const associateResidentQueryResult = await Promise.all(
        // TODO: filter residents which were successfully associated and indicate that in the UI
        selectedResidents.map((resident) =>
          addResidentToUnit({ resident, unitId: id }),
        ),
      );

      if (associateResidentQueryResult.some((result) => 'error' in result)) {
        setAssociateResidentsQueryStatus(QueryStatus.rejected);
      } else {
        setAssociateResidentsQueryStatus(QueryStatus.fulfilled);
      }
    }
    // associate residents - end

    // associate devices - start
    if (selectedDevices.length === 0) {
      setAssociateDevicesQueryStatus(QueryStatus.fulfilled);
    } else if (associateDevicesQueryStatus !== QueryStatus.fulfilled) {
      setDeviceAssociationAttempts((state) => ++state);
      setAssociateDevicesQueryStatus(QueryStatus.pending);

      const associateDevicesResult = await Promise.all(
        // TODO: filter devices which were successfully associated and indicate that in the UI
        selectedDevices.map((device) =>
          addDeviceToUnit({ device, unitId: id }),
        ),
      );

      if (associateDevicesResult.some((result) => 'error' in result)) {
        setAssociateDevicesQueryStatus(QueryStatus.rejected);
      } else {
        setAssociateDevicesQueryStatus(QueryStatus.fulfilled);
      }
    }
    // associate devices - end
  };

  const handleUnitDetailsNextStep = ({ unitName }: { unitName: string }) => {
    setUnitName(unitName);
  };

  const onSelectedDevicesChange = (
    selectedDevices: DeviceAssignedToOrganisation[],
  ) => {
    setSelectedDevices(selectedDevices);
  };

  return (
    <>
      <HeadingGroupContainerLayout className="m-6 justify-center border-0 p-0 text-center">
        <HeadingGroup
          subtitlePrefix={strings.new_unit.subtitle}
          title={strings.new_unit.title}
        />
      </HeadingGroupContainerLayout>
      <Wizard header={<NewUnitWizardHeader stepTitles={stepTitles} />}>
        <UnitDetailsStep
          onCancel={handleCancel}
          onNextStep={handleUnitDetailsNextStep}
          readOnly={createUnitQueryStatus === QueryStatus.fulfilled}
          unitName={unitName}
        />
        <AssociateResidentsStep
          onResidentRemove={handleResidentRemove}
          onResidentSelect={handleResidentSelect}
          selectedResidents={selectedResidents}
        />
        <AssociateDevicesStep
          onSelectedDevicesChange={onSelectedDevicesChange}
          selectedDevices={selectedDevices}
        />
        <ConfirmationStep
          allowSkip={allowSkipDeviceAssociation}
          interactionDisabled={hasPendingQuery}
          onConfirm={handleConfirm}
          onSkip={goToUnitPage}
          residentStepIndex={1}
          selectedDevices={selectedDevices}
          selectedResidents={selectedResidents}
          statusMessage={statusMessage.message}
          statusMessageVariant={statusMessage.variant}
          unitName={unitName}
        />
      </Wizard>

      <FullScreenLoader show={hasPendingQuery} />
    </>
  );
};
